/**
 * API Client
 * Wrapper around Supabase client providing:
 * - Typed query helpers
 * - Consistent error handling
 * - Correlation ID injection
 * - Pagination helpers
 */

import { supabase } from '@/integrations/supabase/client';
import type { ApiResponse, PaginatedResponse, PaginationParams } from '@/types';
import { API, PAGINATION } from '@/config/constants';

// ─── Correlation ID ───────────────────────────────────────

let correlationCounter = 0;

export function generateCorrelationId(): string {
  correlationCounter += 1;
  const timestamp = Date.now().toString(36);
  const counter = correlationCounter.toString(36).padStart(4, '0');
  const random = Math.random().toString(36).substring(2, 6);
  return `${timestamp}-${counter}-${random}`;
}

// ─── Error Normalization ──────────────────────────────────

export function normalizeError(error: unknown): { message: string; code?: string } {
  if (error && typeof error === 'object') {
    const err = error as Record<string, unknown>;
    return {
      message: (err.message as string) ?? 'An unexpected error occurred',
      code: (err.code as string) ?? undefined,
    };
  }
  if (typeof error === 'string') {
    return { message: error };
  }
  return { message: 'An unexpected error occurred' };
}

// ─── Pagination Helper ───────────────────────────────────

export function getPaginationRange(params: PaginationParams): { from: number; to: number } {
  const pageSize = Math.min(params.pageSize, PAGINATION.MAX_PAGE_SIZE);
  const from = (params.page - 1) * pageSize;
  const to = from + pageSize - 1;
  return { from, to };
}

export function buildPaginatedResponse<T>(
  data: T[],
  count: number | null,
  params: PaginationParams,
): PaginatedResponse<T> {
  const total = count ?? 0;
  const pageSize = Math.min(params.pageSize, PAGINATION.MAX_PAGE_SIZE);
  return {
    data,
    total,
    page: params.page,
    pageSize,
    hasMore: params.page * pageSize < total,
  };
}

export async function invokeSupabaseFunction<TResponse = unknown>(
  functionName: string,
  body?: unknown,
): Promise<TResponse> {
  const { data, error } = await supabase.functions.invoke(functionName, {
    body: body as Record<string, unknown>,
    headers: {
      'x-correlation-id': generateCorrelationId(),
    },
  });

  if (error) {
    throw new Error(normalizeError(error).message);
  }

  if (data && typeof data === 'object' && 'error' in data && data.error) {
    throw new Error(typeof data.error === 'string' ? data.error : 'An unexpected error occurred');
  }

  return data as TResponse;
}

// ─── Supabase Re-export ───────────────────────────────────

export { supabase };
